import React, { useState, useEffect, useRef } from 'react';
import { tlRptApi } from '../services/api';
import '../styles/Modal.css';

const SAP_ENTITIES = [
  { code: 'TTPL',  label: 'TTPL (India)' },
  { code: 'THL',   label: 'THL (Transhealth)' },
  { code: 'IHL',   label: 'IHL (Integris)' },
  { code: 'TVHPL', label: 'TVHPL (Transvalve)' },
  { code: 'Dubai', label: 'Dubai (TMDT)' },
];

const BMD_ENTITIES = [
  { code: 'BMD', label: 'BMD (Blue Medical Devices B.V.)' },
];

const MANUAL_ENTITIES = [
  { name: 'translumina_gmbh', code: 'TLG',   label: 'TLG (Translumina GmbH)' },
  { name: 'lamed_germany',    code: 'Lamed', label: 'Lamed Germany' },
];

const ALL_CODES = [
  ...SAP_ENTITIES.map(e => e.code),
  ...BMD_ENTITIES.map(e => e.code),
  ...MANUAL_ENTITIES.map(e => e.code),
];

function formatTimestamp(ts) {
  if (!ts) return null;
  const m = /^(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})/.exec(ts);
  if (!m) return ts;
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${m[3]} ${months[parseInt(m[2], 10) - 1]} ${m[1]} at ${m[4]}:${m[5]}`;
}

function TLRPTModal({ onClose }) {
  const today = new Date();
  const fy = today.getMonth() + 1 >= 4 ? today.getFullYear() : today.getFullYear() - 1;
  const [startDate, setStartDate] = useState(`${fy}-04-01`);
  const [endDate, setEndDate]     = useState(`${fy + 1}-03-31`);

  const [status, setStatus]                   = useState(null);
  const [loadingStatus, setLoadingStatus]     = useState(false);
  const [fetchingEntity, setFetchingEntity]   = useState(null);
  const [uploadingEntity, setUploadingEntity] = useState(null);
  const [downloadingEntity, setDownloadingEntity] = useState(null);
  const [generating, setGenerating]           = useState(false);
  const [genResult, setGenResult]             = useState(null);
  const [error, setError]                     = useState(null);

  // All 8 entities selected by default
  const [selectedEntities, setSelectedEntities] = useState(() => new Set(ALL_CODES));

  const fileInputRef    = useRef(null);
  const [pendingUpload, setPendingUpload] = useState(null);

  useEffect(() => {
    if (startDate && endDate) loadStatus();
  }, [startDate, endDate]);

  const loadStatus = async () => {
    setLoadingStatus(true);
    setError(null);
    try {
      const data = await tlRptApi.status(startDate, endDate);
      setStatus(data);
    } catch (err) {
      console.error('TL RPT status error:', err);
    } finally {
      setLoadingStatus(false);
    }
  };

  const isReady = (code) =>
    status?.entities?.find(e => e.ic_reco_code === code)?.ready ?? false;

  const glUploaded = (entityName) =>
    status?.entities?.find(e => e.entity_name === entityName)?.gl_slots?.general_ledger ?? false;

  const toggleEntity = (code) => {
    setSelectedEntities(prev => {
      const next = new Set(prev);
      next.has(code) ? next.delete(code) : next.add(code);
      return next;
    });
  };

  // ---------- SAP group fetch ----------

  const handleFetchSapGroup = async () => {
    const toFetch = SAP_ENTITIES.map(e => e.code).filter(c => selectedEntities.has(c));
    if (toFetch.length === 0) return;
    setError(null);
    for (const code of toFetch) {
      setFetchingEntity(code);
      try {
        await tlRptApi.fetchSapGL(code, startDate, endDate);
      } catch (err) {
        setError(`Failed to fetch ${code}: ${err.data?.error || err.message}`);
        break;
      }
    }
    setFetchingEntity(null);
    await loadStatus();
  };

  // ---------- BMD group fetch ----------

  const handleFetchBmdGroup = async () => {
    if (!selectedEntities.has('BMD')) return;
    setFetchingEntity('BMD');
    setError(null);
    try {
      await tlRptApi.fetchBmd(startDate, endDate);
      await loadStatus();
    } catch (err) {
      setError(`BMD fetch failed: ${err.data?.error || err.message}`);
    } finally {
      setFetchingEntity(null);
    }
  };

  // ---------- Manual GL upload ----------

  const triggerUpload = (entityName) => {
    setPendingUpload(entityName);
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !pendingUpload) return;
    e.target.value = '';
    setUploadingEntity(pendingUpload);
    setError(null);
    try {
      await tlRptApi.uploadGL(pendingUpload, 'general_ledger', startDate, endDate, file);
      await loadStatus();
    } catch (err) {
      setError(`Upload failed: ${err.data?.error || err.message}`);
    } finally {
      setUploadingEntity(null);
      setPendingUpload(null);
    }
  };

  const handleDownloadTemplate = async (entityName) => {
    try {
      const blob  = await tlRptApi.glTemplate(entityName, 'general_ledger');
      const url   = window.URL.createObjectURL(blob);
      const a     = document.createElement('a');
      a.href      = url;
      a.download  = `${entityName}_general_ledger_template.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Template download failed: ' + (err.data?.error || err.message));
    }
  };

  // ---------- Per-entity GL download ----------

  const handleDownloadEntityGL = async ({ code, isManual = false, entityName = null }) => {
    setDownloadingEntity(code);
    const period = `${startDate.replace(/-/g, '')}_to_${endDate.replace(/-/g, '')}`;
    try {
      let blob, filename;
      if (isManual) {
        blob     = await tlRptApi.downloadManualGL(entityName, startDate, endDate);
        filename = `${entityName}_general_ledger_${period}.xlsx`;
      } else {
        blob     = await tlRptApi.downloadEntityGL(code, startDate, endDate);
        filename = `${code}_rpt_extraction_${period}.xlsx`;
      }
      const url = window.URL.createObjectURL(blob);
      const a   = document.createElement('a');
      a.href    = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(`Download failed for ${code}: ${err.data?.error || err.message}`);
    } finally {
      setDownloadingEntity(null);
    }
  };

  // ---------- Generate ----------

  const handleGenerate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const entities = Array.from(selectedEntities);
      const data = await tlRptApi.generate(startDate, endDate, entities);
      setGenResult(data);
      await loadStatus();
    } catch (err) {
      setError(err.data?.error || err.message || 'RPT generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadRPT = async () => {
    try {
      const blob   = await tlRptApi.download(startDate, endDate);
      const url    = window.URL.createObjectURL(blob);
      const a      = document.createElement('a');
      a.href       = url;
      const period = `${startDate.replace(/-/g, '')}_to_${endDate.replace(/-/g, '')}`;
      a.download   = `tl_consolidated_rpt_${period}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError('Download failed: ' + (err.data?.error || err.message));
    }
  };

  // ---------- Derived state ----------

  const lastOutput    = status?.last_output;
  const lastGenerated = lastOutput ? formatTimestamp(lastOutput.timestamp) : null;
  const canDownload   = !!lastOutput || genResult?.success;
  const busy          = generating || fetchingEntity !== null || uploadingEntity !== null;

  const sapFetchBusy  = SAP_ENTITIES.some(e => fetchingEntity === e.code);
  const bmdFetchBusy  = fetchingEntity === 'BMD';
  const selectedSapCount = SAP_ENTITIES.filter(e => selectedEntities.has(e.code)).length;

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-lg">

        {/* Header */}
        <div className="modal-header">
          <h2>TL Group — Related Party Transactions</h2>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>

        {/* Hidden file input for manual GL upload */}
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          accept=".xlsx,.xls"
          onChange={handleFileChange}
        />

        <div className="modal-body">

          {/* ── Period ── */}
          <div className="form-row" style={{ marginBottom: 16 }}>
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
            </div>
          </div>

          {/* ── SAP HANA ENTITIES ── */}
          <div className="tl-section">
            <div className="tl-section-header">
              <div className="section-title-row">
                <span className="section-icon">🗄</span>
                <span className="section-title">SAP HANA ENTITIES</span>
              </div>
              <button
                className="btn-fetch-group"
                onClick={handleFetchSapGroup}
                disabled={busy || selectedSapCount === 0}
                title={selectedSapCount === 0 ? 'Select at least one SAP entity' : 'Fetch RPT transactions for selected SAP entities'}
              >
                {sapFetchBusy
                  ? <><span className="spinner-xs spinner-white" /> Fetching…</>
                  : <><span className="icon-refresh">↻</span> Fetch RPT Transactions</>}
              </button>
            </div>

            <div className="entity-table">
              {SAP_ENTITIES.map(({ code, label }) => {
                const ready     = isReady(code);
                const fetching  = fetchingEntity === code;
                const selected  = selectedEntities.has(code);
                const dlBusy    = downloadingEntity === code;
                return (
                  <div key={code} className={`entity-row ${ready ? 'row-ready' : 'row-idle'}`}>
                    <label className="entity-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleEntity(code)}
                        disabled={busy}
                        className="entity-checkbox"
                      />
                    </label>
                    <span className="entity-db-icon">🗄</span>
                    <span className="entity-label">{label}</span>
                    <div className="entity-row-right">
                      {fetching
                        ? <span className="badge-fetching"><span className="spinner-xs spinner-blue" /> Fetching…</span>
                        : ready
                          ? <span className="badge-ready">✓ GL Ready</span>
                          : <span className="badge-idle">○ Not fetched</span>}
                      <button
                        className="btn-dl-entity"
                        onClick={() => handleDownloadEntityGL({ code })}
                        disabled={busy || dlBusy || !ready}
                        title={ready ? `Download raw GL for ${label}` : 'Fetch data first'}
                      >
                        {dlBusy ? <span className="spinner-xs spinner-blue" /> : '↓'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── EXACT ONLINE (BMD) ── */}
          <div className="tl-section">
            <div className="tl-section-header">
              <div className="section-title-row">
                <span className="section-icon">🌐</span>
                <span className="section-title">EXACT ONLINE (BMD)</span>
              </div>
              <button
                className="btn-fetch-group"
                onClick={handleFetchBmdGroup}
                disabled={busy || !selectedEntities.has('BMD')}
                title={!selectedEntities.has('BMD') ? 'BMD is not selected' : 'Fetch RPT transactions from Exact Online'}
              >
                {bmdFetchBusy
                  ? <><span className="spinner-xs spinner-white" /> Fetching…</>
                  : <><span className="icon-refresh">↻</span> Fetch RPT Transactions</>}
              </button>
            </div>

            <div className="entity-table">
              {BMD_ENTITIES.map(({ code, label }) => {
                const ready    = isReady(code);
                const fetching = fetchingEntity === code;
                const selected = selectedEntities.has(code);
                const dlBusy   = downloadingEntity === code;
                return (
                  <div key={code} className={`entity-row ${ready ? 'row-ready' : 'row-idle'}`}>
                    <label className="entity-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleEntity(code)}
                        disabled={busy}
                        className="entity-checkbox"
                      />
                    </label>
                    <span className="entity-db-icon">🌐</span>
                    <span className="entity-label">{label}</span>
                    <div className="entity-row-right">
                      {fetching
                        ? <span className="badge-fetching"><span className="spinner-xs spinner-blue" /> Fetching…</span>
                        : ready
                          ? <span className="badge-ready">✓ GL Ready</span>
                          : <span className="badge-idle">○ Not fetched</span>}
                      <button
                        className="btn-dl-entity"
                        onClick={() => handleDownloadEntityGL({ code })}
                        disabled={busy || dlBusy || !ready}
                        title={ready ? `Download raw GL for ${label}` : 'Fetch data first'}
                      >
                        {dlBusy ? <span className="spinner-xs spinner-blue" /> : '↓'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── MANUAL UPLOAD ENTITIES ── */}
          <div className="tl-section tl-section-manual">
            <div className="tl-section-header">
              <div className="section-title-row">
                <span className="section-icon">⬆</span>
                <span className="section-title">MANUAL UPLOAD ENTITIES</span>
              </div>
            </div>

            <div className="entity-table">
              {MANUAL_ENTITIES.map(({ name, code, label }) => {
                const uploaded        = glUploaded(name);
                const isUploadingThis = uploadingEntity === name;
                const selected        = selectedEntities.has(code);
                const dlBusy          = downloadingEntity === code;
                return (
                  <div key={name} className={`entity-row ${uploaded ? 'row-ready' : 'row-idle'}`}>
                    <label className="entity-checkbox-label">
                      <input
                        type="checkbox"
                        checked={selected}
                        onChange={() => toggleEntity(code)}
                        disabled={busy}
                        className="entity-checkbox"
                      />
                    </label>
                    <span className="entity-db-icon">⬆</span>
                    <span className="entity-label">{label}</span>
                    <div className="entity-row-right">
                      <div className="manual-status-col">
                        {uploaded
                          ? <span className="badge-ready">✓ GL Uploaded</span>
                          : <span className="badge-idle">○ Not uploaded</span>}
                        <span className="badge-gl-type">General Ledger</span>
                      </div>
                      <div className="manual-actions">
                        {uploaded && (
                          <button
                            className="btn-dl-entity"
                            onClick={() => handleDownloadEntityGL({ code, isManual: true, entityName: name })}
                            disabled={busy || dlBusy}
                            title={`Download uploaded GL for ${label}`}
                          >
                            {dlBusy ? <span className="spinner-xs spinner-blue" /> : '↓'}
                          </button>
                        )}
                        <button
                          className={`btn btn-sm ${uploaded ? 'btn-outline' : 'btn-outline-green'}`}
                          onClick={() => triggerUpload(name)}
                          disabled={busy}
                        >
                          {isUploadingThis
                            ? <><span className="spinner-xs" /> Uploading…</>
                            : uploaded ? '↺ Re-upload' : '⬆ Upload GL'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Consolidated RPT ── */}
          <div className="tl-rpt-bar">
            {error && (
              <div className="alert alert-danger" style={{ margin: '0 0 10px' }}>
                <strong>Error:</strong> {error}
              </div>
            )}

            {genResult?.success ? (
              <div className="rpt-banner rpt-banner-success">
                ✓ RPT generated — {genResult.total_rows?.toLocaleString() ?? 0} rows across{' '}
                {genResult.entities_processed?.length ?? 0} entities
              </div>
            ) : lastGenerated ? (
              <div className="rpt-banner rpt-banner-info">
                ⓘ Last generated: <strong>{lastGenerated}</strong>
              </div>
            ) : null}

            <div className="rpt-bar-actions">
              {canDownload && (
                <button className="btn btn-secondary btn-lg-action" onClick={handleDownloadRPT} disabled={busy}>
                  ⬇ Download RPT Report
                </button>
              )}
              <button
                className="btn btn-primary btn-lg-action"
                onClick={handleGenerate}
                disabled={busy || selectedEntities.size === 0}
              >
                {generating
                  ? <><span className="spinner-xs" /> Generating…</>
                  : canDownload ? '↺ Re-generate' : '↺ Generate RPT'}
              </button>
            </div>

            {/* Entity readiness chips */}
            <div className="entity-chips">
              {[
                ...SAP_ENTITIES,
                ...BMD_ENTITIES.map(e => ({ ...e, label: 'BMD (Blue Medical)' })),
                ...MANUAL_ENTITIES.map(e => ({ code: e.code, label: e.label })),
              ].map(({ code, label }) => {
                const ready    = isReady(code);
                const selected = selectedEntities.has(code);
                return (
                  <span
                    key={code}
                    className={`entity-chip ${ready ? 'chip-ready' : 'chip-idle'} ${!selected ? 'chip-deselected' : ''}`}
                    title={selected ? undefined : 'Not selected for generate'}
                  >
                    <span className="chip-dot">{ready ? '✓' : '○'}</span> {label}
                  </span>
                );
              })}
            </div>
          </div>

        </div>{/* end modal-body */}

        {/* Footer */}
        <div className="modal-footer">
          {canDownload && (
            <button className="btn btn-primary" onClick={handleDownloadRPT} disabled={busy}>
              ⬇ Download RPT Report
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose} disabled={generating}>
            {genResult?.success ? 'Close' : 'Cancel'}
          </button>
        </div>

      </div>

      <style jsx>{`
        /* ── Section cards ── */
        .tl-section {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          margin-bottom: 12px;
          overflow: hidden;
          background: #fff;
        }
        .tl-section-manual {
          background: #fffbf5;
          border-color: #fed7aa;
        }
        .tl-section-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 8px 14px;
          background: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }
        .tl-section-manual .tl-section-header {
          background: #fff7ed;
          border-bottom-color: #fed7aa;
        }
        .section-title-row {
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .section-icon { font-size: 14px; }
        .section-title {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.6px;
          color: #374151;
          text-transform: uppercase;
        }

        /* ── Group fetch button ── */
        .btn-fetch-group {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12px;
          font-weight: 600;
          color: #fff;
          background: #b91c1c;
          border: none;
          border-radius: 6px;
          padding: 5px 12px;
          cursor: pointer;
          line-height: 1.4;
        }
        .btn-fetch-group:hover:not(:disabled) { background: #991b1b; }
        .btn-fetch-group:disabled { opacity: 0.5; cursor: not-allowed; }
        .icon-refresh { font-size: 13px; }

        /* ── Entity table rows ── */
        .entity-table { padding: 4px 0; }
        .entity-row {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 14px;
          border-bottom: 1px solid #f1f5f9;
          transition: background 0.1s;
        }
        .entity-row:last-child { border-bottom: none; }
        .entity-row:hover { background: #f8fafc; }
        .row-ready { background: #f0fdf4; }
        .row-ready:hover { background: #dcfce7; }
        .row-idle  { background: #fff; }

        /* ── Checkbox ── */
        .entity-checkbox-label {
          display: flex;
          align-items: center;
          cursor: pointer;
          flex-shrink: 0;
        }
        .entity-checkbox {
          width: 15px;
          height: 15px;
          cursor: pointer;
          accent-color: #2563eb;
        }
        .entity-db-icon {
          font-size: 14px;
          flex-shrink: 0;
          line-height: 1;
        }
        .entity-label {
          flex: 1;
          font-size: 13px;
          font-weight: 500;
          color: #374151;
        }

        /* ── Right side of entity row ── */
        .entity-row-right {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-shrink: 0;
        }

        /* ── Status badges ── */
        .badge-ready {
          font-size: 11px;
          font-weight: 600;
          color: #16a34a;
          background: #dcfce7;
          border: 1px solid #bbf7d0;
          border-radius: 10px;
          padding: 2px 8px;
          white-space: nowrap;
        }
        .badge-idle {
          font-size: 11px;
          color: #9ca3af;
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 2px 8px;
          white-space: nowrap;
        }
        .badge-fetching {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #2563eb;
          white-space: nowrap;
        }

        /* ── Per-entity download button ── */
        .btn-dl-entity {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 26px;
          height: 26px;
          background: #f1f5f9;
          border: 1px solid #cbd5e1;
          border-radius: 5px;
          cursor: pointer;
          font-size: 13px;
          color: #475569;
          flex-shrink: 0;
          line-height: 1;
        }
        .btn-dl-entity:hover:not(:disabled) { background: #e2e8f0; color: #1e293b; }
        .btn-dl-entity:disabled { opacity: 0.35; cursor: not-allowed; }

        /* ── Manual entity extra columns ── */
        .manual-status-col {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
        }
        .badge-gl-type {
          font-size: 10px;
          color: #9ca3af;
        }
        .manual-actions {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-shrink: 0;
        }

        /* ── RPT bar ── */
        .tl-rpt-bar {
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          background: #fafafa;
          padding: 12px 16px 10px;
          margin-bottom: 4px;
        }
        .rpt-banner {
          margin-bottom: 10px;
          padding: 9px 13px;
          border-radius: 7px;
          font-size: 13px;
          line-height: 1.5;
        }
        .rpt-banner-success { background: #f0fdf4; color: #15803d; border: 1px solid #bbf7d0; }
        .rpt-banner-info    { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }

        .rpt-bar-actions {
          display: flex;
          gap: 10px;
          margin-bottom: 10px;
          flex-wrap: wrap;
        }
        .btn-lg-action {
          padding: 8px 16px;
          font-size: 13px;
          font-weight: 600;
          border-radius: 7px;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 6px;
          border: none;
        }

        /* ── Entity chips ── */
        .entity-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 5px;
        }
        .entity-chip {
          display: flex;
          align-items: center;
          gap: 3px;
          padding: 2px 9px;
          border-radius: 12px;
          font-size: 11px;
          border: 1px solid;
        }
        .chip-ready    { background: #f0fdf4; border-color: #bbf7d0; color: #15803d; }
        .chip-idle     { background: #f9fafb; border-color: #e5e7eb; color: #9ca3af; }
        .chip-deselected { opacity: 0.4; text-decoration: line-through; }
        .chip-dot      { font-size: 10px; }

        /* ── Buttons ── */
        .btn { cursor: pointer; border-radius: 6px; font-size: 13px; }
        .btn-sm { padding: 5px 10px; font-size: 12px; }
        .btn-outline {
          border: 1px solid #adb5bd; background: white; color: #495057;
        }
        .btn-outline:hover:not(:disabled) { background: #f8f9fa; }
        .btn-outline-green {
          border: 1px solid #40c057; background: white; color: #2f9e44;
        }
        .btn-outline-green:hover:not(:disabled) { background: #ebfbee; }
        .btn:disabled { opacity: 0.5; cursor: not-allowed; }

        /* ── Spinners ── */
        .spinner-xs {
          display: inline-block;
          width: 10px; height: 10px;
          border: 2px solid rgba(255,255,255,0.35);
          border-top-color: currentColor;
          border-radius: 50%;
          animation: spin 0.7s linear infinite;
          vertical-align: middle;
        }
        .spinner-white { border-color: rgba(255,255,255,0.25); border-top-color: #fff; }
        .spinner-blue  { border-color: rgba(59,130,246,0.2);   border-top-color: #3b82f6; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default TLRPTModal;
